import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, CheckCircle2, Package, Undo2, ScanLine, X,
  Trash2, RotateCcw,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert';

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('session_token')}` };
}

export default function ComboV2ReturnExcess() {
  const navigate = useNavigate();
  const { planId } = useParams();

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('usn'); // 'usn' | 'bundle'
  const [usnInput, setUsnInput] = useState('');
  const [bundleInput, setBundleInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [expandedSku, setExpandedSku] = useState({});
  const usnRef = useRef(null);
  const bundleRef = useRef(null);

  const loadSession = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(
        `${API}/api/combo-v2/return-excess/plans/${planId}/scan-session`,
        { headers: authHeaders() }
      );
      setSession(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load session');
    } finally {
      setLoading(false);
    }
  }, [planId]);

  useEffect(() => { loadSession(); }, [loadSession]);

  // Focus the active input on mode changes / after ops
  useEffect(() => {
    if (busy) return;
    if (mode === 'usn') usnRef.current?.focus();
    else bundleRef.current?.focus();
  }, [mode, busy, session?.buffer_count]);

  const handleScanUsn = async () => {
    const usn = usnInput.trim();
    if (!usn) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/api/combo-v2/return-excess/plans/${planId}/scan-usn`,
        { usn_code: usn },
        { headers: authHeaders() }
      );
      toast.success(`Scanned ${r.data.usn.usn_code} (buffer ${r.data.buffer_count})`);
      setUsnInput('');
      await loadSession();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to scan USN');
    } finally {
      setBusy(false);
    }
  };

  const handleUnscan = async (usnCode) => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/combo-v2/return-excess/plans/${planId}/unscan-usn`,
        { usn_code: usnCode },
        { headers: authHeaders() }
      );
      await loadSession();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove USN');
    } finally {
      setBusy(false);
    }
  };

  const handleWrapBundle = async () => {
    const code = bundleInput.trim().toUpperCase();
    if (!code) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/api/combo-v2/return-excess/plans/${planId}/wrap-bundle`,
        { bundle_code: code },
        { headers: authHeaders() }
      );
      toast.success(`Bundle ${code} saved as PENDING PUT AWAY`);
      setBundleInput('');
      setMode('usn');
      if (r.data.plan_completed) {
        toast.success('Plan auto-completed — all leftover USNs returned.');
      }
      await loadSession();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to wrap bundle');
    } finally {
      setBusy(false);
    }
  };

  const handleUndoBundle = async (bundleCode) => {
    if (!window.confirm(`Undo bundle ${bundleCode}? USNs go back to the scan buffer.`)) return;
    setBusy(true);
    try {
      await axios.delete(
        `${API}/api/combo-v2/return-excess/plans/${planId}/bundle/${encodeURIComponent(bundleCode)}`,
        { headers: authHeaders() }
      );
      toast.success('Bundle undone — USNs restored to buffer');
      await loadSession();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to undo bundle');
    } finally {
      setBusy(false);
    }
  };

  if (loading || !session) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const {
    plan_name, plan_status, session_status,
    total_leftover_pcs, remaining_pcs, scanned_into_bundles_pcs,
    buffer_usns, buffer_sku, buffer_count, bundles, pending_by_sku,
  } = session;

  const isCompleted = plan_status === 'COMPLETED' || session_status === 'completed';
  const canWrap = buffer_count > 0 && !isCompleted;

  return (
    <div className="min-h-screen bg-[#FFFBF5] p-4 md:p-6" data-testid="return-excess-page">
      <div className="max-w-5xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-semibold">Return Excess — Continuous Scan</h1>
            <p className="text-xs text-stone-500 font-mono">{planId} • {plan_name || ''}</p>
          </div>
          <Button variant="outline" size="sm" onClick={loadSession} disabled={busy}>
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>

        {/* Plan completed banner */}
        {isCompleted && (
          <Alert className="bg-emerald-50 border-emerald-300" data-testid="plan-completed-alert">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            <AlertTitle className="text-emerald-800">Plan Completed</AlertTitle>
            <AlertDescription className="text-emerald-700">
              All leftover USNs returned. Bundles are queued for SFG Put-Away — the SFG operator
              will scan a bin and tag each bundle's location.
            </AlertDescription>
          </Alert>
        )}

        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-3">
            <div className="text-xs text-stone-500">Total Leftover</div>
            <div className="text-2xl font-bold">{total_leftover_pcs}</div>
          </Card>
          <Card className="p-3">
            <div className="text-xs text-stone-500">Remaining to Scan</div>
            <div className="text-2xl font-bold text-amber-700">{remaining_pcs}</div>
          </Card>
          <Card className="p-3">
            <div className="text-xs text-stone-500">In Buffer (unwrapped)</div>
            <div className="text-2xl font-bold text-blue-700">{buffer_count}</div>
          </Card>
          <Card className="p-3">
            <div className="text-xs text-stone-500">Wrapped into Bundles</div>
            <div className="text-2xl font-bold text-emerald-700">{scanned_into_bundles_pcs}</div>
          </Card>
        </div>

        {/* Pending by SKU */}
        {pending_by_sku && pending_by_sku.length > 0 && !isCompleted && (
          <Card className="p-3">
            <div className="text-xs text-stone-500 mb-2">Pending by SKU — click a row to view the leftover USNs</div>
            <div className="flex flex-col gap-2">
              {pending_by_sku.map((g) => {
                const isOpen = !!expandedSku[g.sku_code];
                return (
                  <div
                    key={g.sku_code}
                    className="border rounded-md"
                    data-testid={`pending-sku-${g.sku_code}`}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedSku((p) => ({ ...p, [g.sku_code]: !p[g.sku_code] }))
                      }
                      className="w-full px-3 py-2 flex items-start justify-between gap-3 text-left hover:bg-stone-50 rounded-md"
                      data-testid={`pending-sku-toggle-${g.sku_code}`}
                    >
                      <div className="flex flex-col text-left">
                        <div className="flex items-center gap-2">
                          <span className="text-stone-400 text-xs w-3 inline-block">
                            {isOpen ? '▼' : '▶'}
                          </span>
                          <span className="font-mono text-xs">{g.sku_code}</span>
                          <span className="text-stone-400">·</span>
                          <span className="font-medium">{g.design_name}</span>
                        </div>
                        <div className="text-xs text-stone-500 mt-0.5 ml-5">
                          {[g.shade, g.size, g.description].filter(Boolean).join(' · ') || '—'}
                        </div>
                      </div>
                      <span className="text-amber-700 font-semibold whitespace-nowrap">
                        {g.pcs} pcs
                      </span>
                    </button>
                    {isOpen && (
                      <div
                        className="px-3 pb-3 pt-1 ml-5 border-t bg-stone-50/50"
                        data-testid={`pending-sku-usns-${g.sku_code}`}
                      >
                        {(g.usns || []).length === 0 ? (
                          <div className="text-xs text-stone-500 py-2">
                            No USN list available (refresh the page).
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-1.5 pt-2">
                            {(g.usns || []).map((u) => (
                              <span
                                key={u}
                                className="px-2 py-0.5 bg-white border rounded text-xs font-mono text-stone-700"
                                data-testid={`pending-usn-${u}`}
                              >
                                {u}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Scan panel */}
        {!isCompleted && (
          <Card className="p-4 space-y-3">
            {/* Mode tabs */}
            <div className="flex gap-2">
              <Button
                variant={mode === 'usn' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setMode('usn')}
                disabled={busy}
                data-testid="mode-usn-btn"
              >
                <ScanLine className="h-4 w-4 mr-1" />
                1. Scan USN
              </Button>
              <Button
                variant={mode === 'bundle' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setMode('bundle')}
                disabled={busy || buffer_count === 0}
                data-testid="mode-bundle-btn"
              >
                <Package className="h-4 w-4 mr-1" />
                2. Wrap Bundle ({buffer_count})
              </Button>
            </div>

            {mode === 'usn' && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Scan leftover USN</label>
                <div className="flex gap-2">
                  <Input
                    ref={usnRef}
                    value={usnInput}
                    onChange={(e) => setUsnInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleScanUsn()}
                    placeholder="Scan or type USN code"
                    disabled={busy}
                    className="font-mono"
                    data-testid="usn-input"
                  />
                  <Button onClick={handleScanUsn} disabled={busy || !usnInput.trim()} data-testid="usn-scan-btn">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Scan'}
                  </Button>
                </div>
                {buffer_sku && (
                  <p className="text-xs text-amber-700">
                    Buffer locked to SKU <span className="font-mono font-semibold">{buffer_sku}</span> —
                    wrap the current bundle to switch SKUs.
                  </p>
                )}
              </div>
            )}

            {mode === 'bundle' && (
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Scan the fresh Bundle Code (from Print Codes)
                </label>
                <div className="flex gap-2">
                  <Input
                    ref={bundleRef}
                    value={bundleInput}
                    onChange={(e) => setBundleInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && canWrap && handleWrapBundle()}
                    placeholder="BUN######/YY-YY"
                    disabled={busy || !canWrap}
                    className="font-mono uppercase"
                    data-testid="bundle-input"
                  />
                  <Button
                    onClick={handleWrapBundle}
                    disabled={busy || !canWrap || !bundleInput.trim()}
                    data-testid="wrap-bundle-btn"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Wrap'}
                  </Button>
                </div>
                <p className="text-xs text-stone-600">
                  Paste the printed bundle sticker on the physical stack, then scan the barcode
                  here. The bundle will be saved as <strong>PENDING PUT AWAY</strong> so the
                  SFG Put-Away operator can bin it.
                </p>
              </div>
            )}
          </Card>
        )}

        {/* Buffer */}
        {buffer_count > 0 && (
          <Card className="p-3" data-testid="buffer-card">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium">
                Buffer — {buffer_count} USN(s){buffer_sku ? ` · SKU ${buffer_sku}` : ''}
              </div>
              <Button
                size="sm"
                variant="default"
                onClick={() => setMode('bundle')}
                disabled={busy || isCompleted}
                data-testid="proceed-to-wrap-btn"
              >
                <Package className="h-4 w-4 mr-1" />
                Proceed to Wrap
              </Button>
            </div>
            <div className="max-h-48 overflow-y-auto divide-y divide-stone-100 rounded border">
              {buffer_usns.map((u) => (
                <div key={u.usn_code} className="px-3 py-1.5 flex items-center justify-between text-sm" data-testid={`buffer-usn-${u.usn_code}`}>
                  <span className="font-mono">{u.usn_code}</span>
                  <span className="text-xs text-stone-500">
                    {u.design_name} · {u.shade || '-'} · {u.size || '-'}
                  </span>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => handleUnscan(u.usn_code)}
                    disabled={busy}
                    className="h-6 w-6"
                    data-testid={`unscan-${u.usn_code}`}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Wrapped bundles */}
        {bundles && bundles.length > 0 && (
          <Card className="p-3">
            <div className="text-sm font-medium mb-2">
              Wrapped Bundles ({bundles.length}) — Pending Put Away
            </div>
            <div className="divide-y divide-stone-100 rounded border">
              {bundles.map((b) => (
                <div key={b.bundle_code} className="px-3 py-2 flex items-center justify-between" data-testid={`wrapped-bundle-${b.bundle_code}`}>
                  <div>
                    <div className="font-mono text-sm font-semibold">{b.bundle_code}</div>
                    <div className="text-xs text-stone-500">
                      SKU {b.sku_code} · {b.design_name} · {(b.usn_codes || []).length} pcs
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300">
                      PENDING PUT AWAY
                    </Badge>
                    {!isCompleted && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleUndoBundle(b.bundle_code)}
                        disabled={busy}
                        className="h-7 w-7"
                        data-testid={`undo-${b.bundle_code}`}
                        title="Undo (move USNs back to buffer)"
                      >
                        <Undo2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
